import React, {useMemo, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {NavLink, useNavigate} from 'react-router-dom';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Input} from '~/components/Controls/Input';
import {useConfirm} from '~/components/Dialogs';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {Intent} from '~/lib/intent';
import {sortByBoolean, sortByNumber, sortByString} from '~/lib/sort';
import {renderBoolean} from '~/lib/helpers';
import {biddersLinks} from '~/modules/Bidders';
import {BiddersAbility} from '~/modules/Bidders/ability';
import {deleteBidderGroup} from '~/modules/Bidders/api';
import {BiddersTypeNavigation} from '~/modules/Bidders/components/BiddersTypeNavigation';
import {IBidderGroup, IVT_SENSITIVE_HELP_TEXT} from '~/modules/Bidders/models/BidderGroup';
import {IBidderLite} from '~/modules/Bidders/models/BidderLite';
import {InfoPopover} from '~/components/InfoPopover';
import {BIDDER_GROUP_QUERY_KEY, useBidderGroupsQuery} from '~/modules/Bidders/queries';
import {TagsList} from '~/components/Controls/Tags';

const distinctStatuses = (bidders: IBidderLite[]): string[] =>
    Array.from(new Set(bidders.map(bidder => bidder.status)));

const extractBidderId = (value: any): number | undefined => {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'object' && value !== null) {
        if (typeof value.value === 'number') {
            return value.value;
        }
        if (typeof value.id === 'number') {
            return value.id;
        }
    }
    return undefined;
};

const normalizeGroup = (group: IBidderGroup): IBidderGroup => ({
    ...group,
    id: extractBidderId(group.id) ?? group.id,
    bidderIds: (group.bidderIds ?? [])
        .map(extractBidderId)
        .filter((id): id is number => typeof id === 'number')
});

export const BidderGroupsView = () => {
    const ability = useAbility(BiddersAbility);
    const navigate = useNavigate();
    const confirm = useConfirm();
    const queryClient = useQueryClient();
    const [filter, setFilter] = useState<{ id: string; name: string }>({id: '', name: ''});
    const apiFilter = useMemo(() => {
        const trimmedId = filter.id?.trim();
        const trimmedName = filter.name?.trim();
        let idValue;
        if (trimmedId) {
            const parsed = Number(trimmedId);
            if (!Number.isNaN(parsed)) {
                idValue = parsed;
            }
        }
        return {
            id: idValue,
            name: trimmedName || undefined
        };
    }, [filter]);

    const groupsQuery = useBidderGroupsQuery(apiFilter);
    const {data: groups = [], isPending} = groupsQuery;
    const records = useMemo(
        () => groups.map(normalizeGroup),
        [groups]
    );

    const refetch = () => groupsQuery.refetch();

    const deleteMutation = useMutation({
        mutationFn: (group: IBidderGroup) => deleteBidderGroup(group),
    });

    const handleDelete = (group: IBidderGroup) => confirm(`Delete bidder group "${group.name}"?`, {
        icon: <i className='fa fa-question'/>,
        intent: Intent.DANGER,
        onOk: () => {
            deleteMutation.mutate(group, {
                onSuccess: () => {
                    const numericId = extractBidderId(group.id);
                    if (typeof numericId === 'number') {
                        queryClient.removeQueries({queryKey: BIDDER_GROUP_QUERY_KEY(numericId), exact: true});
                    }
                    refetch();
                },
            });
        }
    });

    return <div>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs">
                <Breadcrumbs/>
            </div>
            <Title className="page-header-title" toolbar={<>
                {ability.canCreate() && <Button intent={Intent.PRIMARY}
                    leftIcon={<i className='fa fa-plus'/>}
                    disabled={isPending}
                    onClick={() => navigate(biddersLinks.bidderGroupCreate())}
                    responsive
                >Create</Button>}
                <Button intent={Intent.SUCCESS}
                    leftIcon={<i className='fa fa-refresh'/>}
                    disabled={isPending}
                    onClick={refetch}
                    responsive
                >Reload</Button>
            </>}>Bidder groups</Title>
        </div>
        <BiddersTypeNavigation/>
        <div className="layout-content-box">
            <form style={{display: 'flex', gap: '10px', marginBottom: '10px'}}
                onSubmit={event => event.preventDefault()}
            >
                <Input
                    placeholder='Filter by ID'
                    value={filter.id}
                    onChange={value => setFilter(prev => ({...prev, id: value}))}
                    disabled={isPending}
                    rightIcon={<i className='fa fa-search input-search-icon' />}
                />
                <Input
                    placeholder='Filter by name'
                    value={filter.name}
                    onChange={value => setFilter(prev => ({...prev, name: value}))}
                    disabled={isPending}
                    rightIcon={<i className='fa fa-search input-search-icon' />}
                />
            </form>
            <TableSort records={records}
            header
            defaultSort={{
                sort: sortByNumber,
                field: 'id',
                direction: TableSortDirection.DESC
            }}
            columns={[{
                header: 'ID',
                field: 'id',
                sort: sortByNumber,
                small: true
            }, {
                header: 'Name',
                field: 'name',
                sort: sortByString,
                cellAttrs: {style: {width: '100%'}},
                cell: group => typeof group.id === 'number'
                    ? <NavLink to={biddersLinks.bidderGroup(group.id)}>{group.name}</NavLink>
                    : group.name
            }, {
                header: <>IVT sensitive <InfoPopover content={IVT_SENSITIVE_HELP_TEXT}/></>,
                field: 'ivtSensitive',
                sort: sortByBoolean,
                small: true,
                cellAttrs: {style: {textAlign: 'center'}},
                cell: group => renderBoolean(group.ivtSensitive)
            }, {
                header: 'Bidder statuses',
                responsive: true,
                cell: group => group.ivtSensitive && group.bidders?.length
                    ? <TagsList values={distinctStatuses(group.bidders)} size="small"/>
                    : null
            }, {
                cell: group => <TableRowActions>
                    {ability.canEdit() && typeof group.id === 'number' && <Button
                        small
                        variant="menu"
                        onClick={() => navigate(biddersLinks.bidderGroup(group.id!))}
                        leftIcon={<i className='fa fa-edit'/>}
                    >Edit</Button>}
                    {ability.canDelete() && <Button
                        small
                        variant="menu"
                        intent={Intent.DANGER}
                        onClick={() => handleDelete(group)}
                        leftIcon={<i className='fa fa-trash'/>}
                    >Delete</Button>}
                </TableRowActions>,
                short: true
            }]}
            />
        </div>
    </div>;
};
